import React from 'react';
import { Box, Paper, Typography, Divider } from '@mui/material';
import FieldRenderer from './FieldRenderer';
import ColumnLayout from './ColumnLayout';
import { PageTitle } from '../StyledComponents';

const FormBlock = (props) => {
  // Section heading style: when the schema declares a non-h4 variant (e.g.
  // `variant: 'h6'` on a Traveler / Contact / Expenses section header), render
  // a compact section title with a Tyto Deep Blue accent stripe — no big
  // PageTitle, no underline divider. Top-level page headings (no variant, or
  // explicit h4) still use PageTitle so the page banner reads as the largest
  // element on the page.
  const renderHeading = () => {
    const isPageTitle = !props.block.variant || props.block.variant === 'h4';
    // note may be a string or a (formData) => string function so headings can
    // react to form state (e.g. approval-chain note flipping based on the
    // selected workflow type).
    const noteText =
      typeof props.block.note === 'function'
        ? props.block.note(props.formData)
        : props.block.note;

    if (isPageTitle) {
      return (
        <Box>
          <PageTitle gutterBottom>{props.block.label}</PageTitle>
          {noteText && (
            <Typography variant="body2" color="text.secondary">
              {noteText}
            </Typography>
          )}
          {props.block.divider !== false && (
            <Divider sx={{ mt: 1, mb: 0.5 }} />
          )}
        </Box>
      );
    }

    const Icon = props.block.icon;
    // subText renders on a separate line below the title (e.g. the
    // approval-chain blurb under "Signatures & Approval Chain"). `note`
    // continues to render inline on the right of the title.
    const subTextValue =
      typeof props.block.subText === 'function'
        ? props.block.subText(props.formData)
        : props.block.subText;
    // headerField renders a real form field inline on the right of the
    // heading title (e.g. an "Approval Due Date" picker beside the
    // Signatures & Approval Chain header).
    const headerField = props.block.headerField;
    // When the heading has a headerField, restructure into a two-column
    // flex layout so the field is vertically centered against the
    // combined title + subText stack on the left.
    if (headerField) {
      return (
        <Box
          sx={{
            mt: 1,
            mb: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderLeft: '3px solid #1F4D83',
            pl: 1.5,
            py: 0.25,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              {Icon && (
                <Icon
                  sx={{ color: '#1F4D83', fontSize: '1.25rem', flexShrink: 0 }}
                />
              )}
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: 'text.primary' }}
              >
                {props.block.label}
              </Typography>
              {noteText && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 'auto', textAlign: 'right', alignSelf: 'flex-start' }}
                >
                  {noteText}
                </Typography>
              )}
            </Box>
            {subTextValue && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {subTextValue}
              </Typography>
            )}
          </Box>
          <Box sx={{ flexShrink: 0, minWidth: 0, width: 200, alignSelf: 'center' }}>
            <FieldRenderer
              field={headerField}
              formData={props.formData}
              setFormData={props.setFormData}
              dynamicOptions={props.dynamicOptions}
              dirtyMap={props.dirtyMap}
              setDirtyMap={props.setDirtyMap}
              readOnly={props.readOnly}
            />
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 1, mb: 0.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            borderLeft: '3px solid #1F4D83',
            pl: 1.5,
            py: 0.25,
          }}
        >
          {Icon && (
            <Icon
              sx={{ color: '#1F4D83', fontSize: '1.25rem', flexShrink: 0 }}
            />
          )}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: 'text.primary' }}
          >
            {props.block.label}
          </Typography>
          {noteText && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ml: 'auto', textAlign: 'right', alignSelf: 'flex-start' }}
            >
              {noteText}
            </Typography>
          )}
        </Box>
        {subTextValue && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ pl: 1.875, mt: 0.5 }}
          >
            {subTextValue}
          </Typography>
        )}
      </Box>
    );
  };

  const renderGroup = () => {
    // In readOnly mode, hide groups that only contain fileUpload fields with no uploaded files
    if (props.readOnly && props.block.fields) {
      const hasOnlyFileFields = props.block.fields.every(field => field.type === 'fileUpload');
      if (hasOnlyFileFields) {
        // Check if any file field has files
        const hasAnyFiles = props.block.fields.some(field => {
          const files = props.formData?.[field.name];
          return Array.isArray(files) && files.length > 0;
        });
        if (!hasAnyFiles) {
          return null; // Hide the entire section
        }
      }
    }

    // When this group is rendered inside a section card (DynamicForm bundles
    // a heading + following groups into one Paper), drop the per-group Paper
    // wrapper — the whole section reads as ONE solid card. Outside a section
    // card (legacy / pre-section blocks), keep the outlined Paper so groups
    // still have their own chrome.
    const Wrapper = props.inSectionCard ? Box : Paper;
    const wrapperSx = props.inSectionCard
      ? { mt: 1.5 }
      : { p: 2, borderRadius: 1, mt: 1 };
    const wrapperProps = props.inSectionCard ? {} : { variant: 'outlined' };

    return (
    <Wrapper {...wrapperProps} sx={wrapperSx}>
      {props.block.label && (
        <Typography variant="subtitle1" gutterBottom>
          {props.block.label}
        </Typography>
      )}
      <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="flex-start">
        {props.block.fields.map((field, idx) => {
          const itemFlex = field.flex || '1 1 280px';
          // Spacer: invisible Box that consumes flex space — useful for
          // mirroring multi-column layouts in single-field rows so the
          // visible field's width matches a peer field in another section.
          if (field.type === 'spacer') {
            return (
              <Box
                key={field.name || `spacer-${idx}`}
                flex={itemFlex}
                minWidth={0}
                aria-hidden
              />
            );
          }
          return (
          <Box key={field.name} flex={itemFlex} minWidth={240}>
            <FieldRenderer
              field={field}
              formData={props.formData}
              setFormData={props.setFormData}
              dynamicOptions={props.dynamicOptions}
              dirtyMap={props.dirtyMap}
              setDirtyMap={props.setDirtyMap}
              readOnly={props.readOnly}
            />
          </Box>
        );})}
      </Box>
    </Wrapper>
  );};

  // Table layout removed per redesign. Use groups with horizontal layout instead.

  const blockTypes = {
    heading: renderHeading,
    group: renderGroup,
    table: renderGroup,
    undefined: renderGroup,
    nestedexpandable: () => (
      <Box>
        <FieldRenderer
          field={props.block}
          formData={props.formData}
          setFormData={props.setFormData}
          dynamicOptions={props.dynamicOptions}
          dirtyMap={props.dirtyMap}
          setDirtyMap={props.setDirtyMap}
          readOnly={props.readOnly}
        />
      </Box>
    ),
    repeatableSection: () => (
      <Box>
        <FieldRenderer
          field={props.block}
          formData={props.formData}
          setFormData={props.setFormData}
          dynamicOptions={props.dynamicOptions}
          dirtyMap={props.dirtyMap}
          setDirtyMap={props.setDirtyMap}
          readOnly={props.readOnly}
        />
      </Box>
    ),
    simpleTable: () => (
      <Box>
        <FieldRenderer
          field={props.block}
          formData={props.formData}
          setFormData={props.setFormData}
          dynamicOptions={props.dynamicOptions}
          dirtyMap={props.dirtyMap}
          setDirtyMap={props.setDirtyMap}
          readOnly={props.readOnly}
        />
      </Box>
    ),
    columnLayout: () => (
      <ColumnLayout
        block={props.block}
        formData={props.formData}
        setFormData={props.setFormData}
        dynamicOptions={props.dynamicOptions}
        dirtyMap={props.dirtyMap}
        setDirtyMap={props.setDirtyMap}
        readOnly={props.readOnly}
      />
    ),
  };

  const renderBlock = blockTypes[props.block.type];
  return renderBlock ? renderBlock() : null;
};

export default FormBlock;
